import { Alert, Box, Button, Card, CircularProgress, Container, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Iconify from "src/components/iconify";
import VoucherTable from "src/components/transactions-table/VoucherTable";
import { get, post } from "src/utils/networkUtils";
import Scrollbar from "../components/scrollbar";

export default function VoucherPage() {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [vouchers, setVouchers] = useState([]);
	const [statusFilter, setStatusFilter] = useState("");
	const [userIdFilter, setUserIdFilter] = useState("");
	const [gameIdFilter, setGameIdFilter] = useState("");

	const [amount, setAmount] = useState<number | null>(null);

	useEffect(() => {
		get(`/admin/vouchers?offset=0&limit=2000`).then((response) => {
			setVouchers(response.vouchers);
			setLoading(false);
		});
	}, []);

	const handleApplyFilters = () => {
		setLoading(true);

		if (statusFilter) {
			get(`/admin/vouchers/status/${statusFilter}?offset=0&limit=2000`).then((response) => {
				setVouchers(response.vouchers);
				setLoading(false);
			});
			return;
		}

		if (userIdFilter) {
			get(`/admin/vouchers/user/${userIdFilter}?offset=0&limit=2000`).then((response) => {
				setVouchers(response.vouchers);
				setLoading(false);
			});
			return;
		}

		if (gameIdFilter) {
			get(`/admin/vouchers/game/${gameIdFilter}?offset=0&limit=2000`).then((response) => {
				setVouchers(response.vouchers);
				setLoading(false);
			});
			return;
		}

		get(`/admin/vouchers?offset=0&limit=2000`).then((response) => {
			setVouchers(response?.vouchers || []);
			setLoading(false);
		});
	};

	const handleClearFilters = () => {
		setStatusFilter("");
		setUserIdFilter("");
		setGameIdFilter("");
		setLoading(true);

		get(`/admin/vouchers`).then((response) => {
			setVouchers(response.vouchers);
			setLoading(false);
		});
	};

	const uploadVouchers = (event) => {
		if (!amount) {
			setError("Amount is required");
			return;
		}

		if (event.target.files.length > 0) {
			setLoading(true);
			const files = event.target.files;

			const bodyFormData = new FormData();
			bodyFormData.append("file", files[0]);

			post(`/admin/vouchers/${amount}`, bodyFormData, {
				headers: { "Content-Type": "multipart/form-data" },
			})
				.then(() => {
					window.location.reload();
				})
				.catch((error) => {
					setError(error?.response?.data?.message || "Something went wrong");
					setLoading(false);
				});
		}
	};

	return (
		<>
			<Helmet>
				<title>Vouchers</title>
			</Helmet>

			<Container>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Typography variant="h4" gutterBottom>
						Vouchers
					</Typography>

					<Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
						<TextField label="Amount" type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} />
						<Button color="primary" aria-label="upload picture" component="label">
							<input hidden accept="text/csv" type="file" onChange={(event) => uploadVouchers(event)} />
							<Iconify icon={"eva:upload-outline"} sx={{ mr: 2 }} />
							Upload Vouchers
						</Button>
					</Card>
				</Stack>

				{error && (
					<Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
						{error}
					</Alert>
				)}

				<Stack direction="row" spacing={2} mb={3} alignItems="center">
					<TextField
						select
						label="Status"
						value={statusFilter}
						onChange={(e) => setStatusFilter(e.target.value)}
						sx={{ minWidth: 150 }}
						disabled={userIdFilter !== "" || gameIdFilter !== ""}
					>
						<MenuItem value="">All</MenuItem>
						<MenuItem value="0">UNUSED</MenuItem>
						<MenuItem value="1">USED</MenuItem>
					</TextField>

					<TextField
						label="User ID"
						type="number"
						value={userIdFilter}
						onChange={(e) => setUserIdFilter(e.target.value)}
						disabled={statusFilter !== "" || gameIdFilter !== ""}
					/>

					<TextField
						label="Game ID"
						type="number"
						value={gameIdFilter}
						onChange={(e) => setGameIdFilter(e.target.value)}
						disabled={statusFilter !== "" || userIdFilter !== ""}
					/>

					<Button variant="contained" color="primary" size="large" onClick={handleApplyFilters}>
						Save
					</Button>
					<Button variant="outlined" color="secondary" size="large" onClick={handleClearFilters}>
						Reset
					</Button>
				</Stack>

				<Card>
					{loading || vouchers === null ? (
						<Box sx={{ display: "flex", justifyContent: "center" }} p={3}>
							<CircularProgress />
						</Box>
					) : (
						<Scrollbar>{<VoucherTable vouchers={vouchers} />}</Scrollbar>
					)}
				</Card>
			</Container>
		</>
	);
}
